import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { useExternalCommentsDA } from '../../hooks/useApiQueries';
import CommentsSummary from './CommentsSummary';
import ExternalCommentsDrawer from './ExternalCommentsDrawer';
import InternalCommentsDrawer from './InternalCommentsDrawer';

interface InternalComment {
  user: string;
  date: string;
  comment: string;
}

interface ExternalComment {
  comment_id: number;
  registration_id: number;
  user_id: number;
  user_role: string;
  user_name: string;
  comment: string;
  created_at: string;
}

interface CommentsManagerProps {
  registrationId: number;
  internalComments: InternalComment[];
  userRole: string;
  type: 'cg' | 'da';
  refetch: () => Promise<void>;
  maxWidth?: number;
  regPlate?: string;
}

const CommentsManager: React.FC<CommentsManagerProps> = ({
  registrationId,
  internalComments,
  userRole,
  type,
  refetch,
  maxWidth = 200,
  regPlate,
}) => {
  const [internalDrawerOpen, setInternalDrawerOpen] = useState(false);
  const [externalDrawerOpen, setExternalDrawerOpen] = useState(false);
  const [externalComments, setExternalComments] = useState<ExternalComment[]>([]);

  const canAddInternalComment = userRole === 'god' || userRole === 'arbitrator';

  // Pour les DA, on utilise le hook existant, pour les CG on fait un fetch manuel
  const { data: daExternalComments } = type === 'da' ? useExternalCommentsDA(registrationId) : { data: undefined };

  useEffect(() => {
    if (type === 'da' && daExternalComments) {
      setExternalComments(daExternalComments);
    } else if (type === 'cg') {
      fetchCGExternalComments();
    }
  }, [type, daExternalComments, registrationId]);

  const fetchCGExternalComments = async () => {
    try {
      const response = await makeAuthenticatedApiCall('get', `/api/cg/external-comments/${registrationId}`);
      setExternalComments(response.data as ExternalComment[]);
    } catch (error) {
      console.error('Erreur lors du chargement des commentaires externes CG:', error);
    }
  };

  // Callback pour rafraîchir les commentaires internes
  const handleInternalCommentAdded = async () => {
    await refetch(); // Rafraîchit les commentaires internes via la prop refetch
  };

  // Callback pour rafraîchir les commentaires externes
  const handleExternalCommentAdded = async () => {
    if (type === 'da') {
      // Pour les DA, le hook useExternalCommentsDA se met à jour automatiquement
      // via l'invalidation du cache dans ExternalCommentsDrawer
    } else {
      // Pour les CG, on refetch manuellement
      await fetchCGExternalComments();
    }
  };

  return (
    <Box sx={{ minWidth: maxWidth }}>
      {/* Résumé des commentaires internes */}
      {userRole !== 'agency' && (
        <Box sx={{ mb: 2 }}>
          <CommentsSummary
            comments={internalComments}
            commentType="internal"
            onViewComments={() => setInternalDrawerOpen(true)}
            onAddComment={canAddInternalComment ? () => setInternalDrawerOpen(true) : undefined}
            maxWidth={maxWidth}
            showAddButton={canAddInternalComment}
            viewerRole={userRole}
          />
        </Box>
      )}

      {/* Résumé des commentaires externes */}
      <Box>
        <CommentsSummary
          comments={externalComments.map((comment) => ({
            user_name: comment.user_name,
            created_at: comment.created_at,
            comment: comment.comment,
            user_role: comment.user_role,
          }))}
          commentType="external"
          onViewComments={() => setExternalDrawerOpen(true)}
          onAddComment={() => setExternalDrawerOpen(true)}
          maxWidth={maxWidth}
          showAddButton={true}
          registrationId={registrationId}
          viewerRole={userRole}
        />
      </Box>

      {/* Drawer pour commentaires internes */}
      {userRole !== 'agency' && (
        <InternalCommentsDrawer
          open={internalDrawerOpen}
          onClose={() => setInternalDrawerOpen(false)}
          registrationId={registrationId}
          comments={internalComments}
          refetch={handleInternalCommentAdded}
          type={type}
          onCommentAdded={handleExternalCommentAdded}
          regPlate={regPlate}
        />
      )}

      {/* Drawer pour commentaires externes */}
      <ExternalCommentsDrawer
        open={externalDrawerOpen}
        onClose={() => setExternalDrawerOpen(false)}
        registrationId={registrationId}
        userRole={userRole}
        type={type}
        onCommentAdded={handleExternalCommentAdded}
        regPlate={regPlate}
      />
    </Box>
  );
};

export default CommentsManager;
